import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../pages/auth/AuthContext';
import Status from '../../components/paket-saya/status';
import PackageList from '../../components/paket-saya/paket_aktif';

import Footer from '../../components/footer/index';
import { useDarkMode } from '../../components/darkmode/DarkModeContext';

import { 
  WifiIcon, 
  SignalIcon, 
  ClockIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

// API Configuration
const API_BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:5000') + '/api';

const MyPackages = () => {
  const location = useLocation();
  const { user, token, isAuthenticated } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('aktif');
  const [newOrderMessage, setNewOrderMessage] = useState('');
  const [error, setError] = useState(null);
  const { isDarkMode } = useDarkMode();

  // Helper function untuk mendapatkan data user yang konsisten
  const getActualUserData = () => {
    if (!user) return null;
    
    // Handle nested user structure
    if (user.user && typeof user.user === 'object') {
      if (user.user.profile && typeof user.user.profile === 'object') {
        return user.user.profile;
      }
      return user.user;
    }
    
    // Handle direct profile structure
    if (user.profile && typeof user.profile === 'object' && !user.email && !user.fullName) {
      return user.profile;
    }
    
    // Handle direct user data
    return user;
  };

  const userData = getActualUserData();
  const authToken = token || sessionStorage.getItem('auth_token') || sessionStorage.getItem('token');
  const userId = userData?._id || userData?.id || null;

  // Check if there's a new order from the form
  useEffect(() => {
    if (location.state?.newOrder) {
      setNewOrderMessage(location.state.message || 'Order baru berhasil dibuat!');
      
      // Clear the message after 10 seconds
      setTimeout(() => {
        setNewOrderMessage('');
      }, 10000);
    }
  }, [location.state]);

  // Enhanced fetch function with better error handling
  const fetchUserOrders = async () => {
    setLoading(true);
    setError(null);

    try {
      let transformedPackages = [];
      
      console.log('Fetching orders for user:', {
        userId,
        userEmail: userData?.email,
        userPhone: userData?.phone,
        isAuthenticated,
        hasToken: !!authToken,
        userData: userData
      });

      // Always try to fetch data if we have user info
      if (!userData?.email && !userData?.phone && !userId) {
        // console.log('No user identifier found');
        setError('Data user tidak lengkap. Silakan login ulang.');
        setPackages([]);
        return;
      }

      // Try authenticated endpoint first if we have token and userId
      if (authToken && userId) {
        try {
          // console.log('Trying authenticated endpoint...');
          const response = await fetch(`${API_BASE_URL}/orders/user/my-orders`, {
            method: 'GET',
            headers: {  
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken}`
            },
            credentials: 'include'
          });

          // console.log('My-orders response status:', response.status);
          
          if (response.ok) {
            const result = await response.json();
            // console.log('My-orders API response:', result);
            
            if (result.success && result.data && Array.isArray(result.data) && result.data.length > 0) {
              transformedPackages = transformOrdersToPackages(result.data);
              // console.log('Transformed packages from my-orders:', transformedPackages);
            }
          } else {
            // console.log('Authenticated endpoint failed with status:', response.status);
            const errorText = await response.text();
            // console.log('Error response:', errorText);
          }
        } catch (authError) {
          console.error('Authenticated request failed:', authError);
        }
      }

      // Try customer endpoint if no data from authenticated endpoint or no auth
      if (transformedPackages.length === 0 && (userData?.phone || userData?.email)) {
        try {
          // console.log('Trying customer endpoint...');
          const queryParams = new URLSearchParams();
          
          if (userData.email) {
            queryParams.append('email', userData.email);
            // console.log('Using email:', userData.email);
          }
          if (userData.phone) {
            queryParams.append('phone', userData.phone);
            // console.log('Using phone:', userData.phone);
          }
          
          const customerUrl = `${API_BASE_URL}/orders/customer?${queryParams.toString()}`;
          // console.log('Customer endpoint URL:', customerUrl);

          const response = await fetch(customerUrl, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            }
          });

          // console.log('Customer orders response status:', response.status);

          if (response.ok) {
            const result = await response.json();
            // console.log('Customer orders API response:', result);
            
            // Handle different response structures
            let ordersData = [];
            if (result.success && result.data) {
              ordersData = Array.isArray(result.data) ? result.data : [result.data];
            } else if (result.orders) {
              ordersData = Array.isArray(result.orders) ? result.orders : [result.orders];
            } else if (Array.isArray(result)) {
              ordersData = result;
            }
            
            // console.log('Extracted orders data:', ordersData);
            
            if (ordersData.length > 0) {
              transformedPackages = transformOrdersToPackages(ordersData);
              // console.log('Transformed packages from customer endpoint:', transformedPackages);
            }
          } else {
            // console.log('Customer endpoint failed with status:', response.status);
            const errorText = await response.text();
            // console.log('Error response:', errorText);
          }
        } catch (customerError) {
          // console.error('Customer endpoint failed:', customerError);
        }
      }

      // Final result handling
      if (transformedPackages.length === 0) {
        // console.log('No packages found from any endpoint');
        setError('Tidak ada paket ditemukan. Silakan order paket baru.');
        setPackages([]);
      } else {
        // console.log('Setting packages:', transformedPackages);
        setPackages(transformedPackages);
        setError(null);
      }

    } catch (error) {
      // console.error('Error fetching user orders:', error);
      setError('Gagal mengambil data paket. Silakan coba lagi.');
      setPackages([]);
    } finally {
      setLoading(false);
    }
  };

  // Enhanced transformation function
  const transformOrdersToPackages = (orders) => {
    // console.log('Transforming orders:', orders);
    
    if (!Array.isArray(orders)) {
      console.error('Orders is not an array:', orders);
      return [];
    }
    
    return orders.map((order, index) => {
      // console.log(`Transforming order ${index}:`, order);
      
      // Extract package info from different possible locations
      const packageInfo = order.packageSnapshot || order.package || order.packageInfo || {};
      const addressInfo = order.address || order.installationAddress || {};
      const customerInfo = order.customer || order.customerInfo || {};
      
      // Better price extraction with more fallbacks
      let monthlyFee = 0;
      if (packageInfo.price?.amount) {
        monthlyFee = packageInfo.price.amount;
      } else if (packageInfo.monthlyFee) {
        monthlyFee = packageInfo.monthlyFee;
      } else if (order.pricing?.packagePrice) {
        monthlyFee = order.pricing.packagePrice;
      } else if (order.pricing?.totalAmount) {
        monthlyFee = order.pricing.totalAmount;
      } else if (order.totalAmount) {
        monthlyFee = order.totalAmount;
      } else if (order.price) {
        monthlyFee = order.price;
      }

      // Better location formatting
      let location = 'Alamat tidak tersedia';
      if (addressInfo.fullAddress) {
        location = addressInfo.fullAddress;
      } else if (typeof addressInfo === 'string') {
        location = addressInfo;
      } else if (addressInfo.street || addressInfo.city || addressInfo.province) {
        const parts = [
          addressInfo.street,
          addressInfo.village,
          addressInfo.district,
          addressInfo.city,
          addressInfo.province
        ].filter(Boolean);
        location = parts.join(', ');
      } else if (order.address && typeof order.address === 'string') {
        location = order.address;
      }

      // Better package name extraction
      let packageName = 'Paket Internet';
      if (packageInfo.name) {
        packageName = packageInfo.name;
      } else if (packageInfo.title) {
        packageName = packageInfo.title;
      } else if (order.packageName) {
        packageName = order.packageName;
      } else if (packageInfo.speed) {
        packageName = `Paket ${packageInfo.speed}`;
      }

      // Better speed extraction
      let speed = 'Unknown';
      if (packageInfo.speed) {
        speed = packageInfo.speed;
      } else if (packageInfo.bandwidth) {
        speed = packageInfo.bandwidth;
      }

      const transformedPackage = {
        id: order._id || order.id || `order_${index}`,
        orderNumber: order.orderNumber || order.orderCode || `ORD-${Date.now()}-${index}`,
        name: packageName,
        type: determinePackageType(packageName, speed),
        speed: speed,
        status: mapOrderStatusToPackageStatus(order.status),
        startDate: order.schedule?.activationDate || order.activationDate || order.createdAt,
        endDate: calculateEndDate(order.schedule?.activationDate || order.activationDate || order.createdAt),
        monthlyFee: monthlyFee,
        dataUsage: {
          used: Math.floor(Math.random() * 500),
          total: "Unlimited"
        },
        location: location,
        lastPayment: order.schedule?.activationDate || order.activationDate || order.createdAt,
        nextPayment: calculateNextPayment(order.schedule?.activationDate || order.activationDate || order.createdAt),
        uptime: ['active', 'installed', 'confirmed'].includes(order.status) ? 99.5 + Math.random() * 0.4 : 0,
        description: packageInfo.description || order.notes?.customer || packageInfo.features?.join(', ') || 'Paket internet berkualitas tinggi',
        timeline: order.statusHistory || [],
        createdAt: order.createdAt,
        updatedAt: order.updatedAt,
        // Add raw order data for debugging
        rawOrder: order
      };
      
      // console.log(`Transformed package ${index}:`, transformedPackage);
      return transformedPackage;
    });
  };

  // Helper functions
  const determinePackageType = (name = '', speed = '') => {
    const nameSpeed = (name + speed).toLowerCase();
    if (nameSpeed.includes('dedicated') || nameSpeed.includes('business')) return 'dedicated';
    if (nameSpeed.includes('gaming')) return 'broadband';
    if (nameSpeed.includes('satelit') || nameSpeed.includes('starlink')) return 'satelit';
    return 'broadband';
  };

  const calculateEndDate = (startDate) => {
    if (!startDate) return null;
    const date = new Date(startDate);
    date.setFullYear(date.getFullYear() + 1);
    return date.toISOString();
  };

  const calculateNextPayment = (startDate) => {
    if (!startDate) return null;
    const date = new Date(startDate);
    date.setMonth(date.getMonth() + 1);
    return date.toISOString();
  };

  const mapOrderStatusToPackageStatus = (orderStatus) => {
    const statusMap = {
      'pending': 'pending',
      'confirmed': 'aktif', // Changed from 'pending' to 'aktif'
      'survey_scheduled': 'pending',
      'survey_completed': 'pending',
      'installation_scheduled': 'pending',
      'installed': 'aktif',
      'active': 'aktif',
      'suspended': 'suspend',
      'cancelled': 'expired',
      'completed': 'aktif' // Added common status
    };
    return statusMap[orderStatus] || 'pending';
  };

  // Modified useEffect to always try fetching if we have user data
  useEffect(() => {
    // console.log('useEffect triggered:', { user, userData, isAuthenticated });
    
    // Try to fetch data if we have any user information
    if (userData?.email || userData?.phone || isAuthenticated) {
      fetchUserOrders();
    } else {
      setLoading(false);
      setError('Silakan login untuk melihat paket Anda');
      setPackages([]);
    }
  }, [user, authToken, isAuthenticated]);

  const getStatusBadge = (status) => {
    const badges = {
      aktif: {
        icon: <CheckCircleIcon className="h-4 w-4" />,
        text: 'Aktif',
        classes: 'bg-green-100 text-green-800 border-green-200'
      },
      suspend: {
        icon: <ExclamationTriangleIcon className="h-4 w-4" />,
        text: 'Suspend',
        classes: 'bg-red-100 text-red-800 border-red-200'
      },
      pending: {
        icon: <ClockIcon className="h-4 w-4" />,
        text: 'Pending',
        classes: 'bg-yellow-100 text-yellow-800 border-yellow-200'
      },
      expired: {
        icon: <XCircleIcon className="h-4 w-4" />,
        text: 'Expired',
        classes: 'bg-gray-100 text-gray-800 border-gray-200'
      }
    };

    const badge = badges[status] || badges.pending;
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${badge.classes}`}>
        {badge.icon}
        <span className="ml-1">{badge.text}</span>
      </span>
    );
  };

  const getTypeIcon = (type) => {
    const icons = {
      broadband: <WifiIcon className="h-6 w-6 text-blue-500" />,
      dedicated: <SignalIcon className="h-6 w-6 text-purple-500" />,
      satelit: <ArrowPathIcon className="h-6 w-6 text-orange-500" />
    };
    return icons[type] || <WifiIcon className="h-6 w-6 text-gray-500" />;
  };

  const formatCurrency = (amount) => {
    if (!amount || isNaN(amount)) return 'Rp 0';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Tidak tersedia';
    try {
      return new Date(dateString).toLocaleDateString('id-ID', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return 'Tidak tersedia';
    }
  };

  const getFilteredPackages = () => {
    return packages.filter(pkg => {
      if (selectedTab === 'aktif') return pkg.status === 'aktif';
      if (selectedTab === 'tidak-aktif') return ['suspend', 'expired'].includes(pkg.status);
      if (selectedTab === 'pending') return pkg.status === 'pending';
      return true;
    });
  };

  const tabs = [
    { id: 'aktif', name: 'Aktif', count: packages.filter(p => p.status === 'aktif').length },
    { id: 'tidak-aktif', name: 'Tidak Aktif', count: packages.filter(p => ['suspend', 'expired'].includes(p.status)).length },
    { id: 'pending', name: 'Pending', count: packages.filter(p => p.status === 'pending').length },
    { id: 'semua', name: 'Semua', count: packages.length }
  ];

  const refreshData = async () => {
    await fetchUserOrders();
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} pt-20`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-6">
            <div className={`h-8 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'} rounded w-1/4`}></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-md p-6 space-y-4`}>
                  <div className={`h-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'} rounded`}></div>
                  <div className={`h-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'} rounded w-2/3`}></div>
                  <div className={`h-20 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'} rounded`}></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Debug info (remove in production)
  console.log('Render state:', {
    loading,
    packages: packages.length,
    error,
    userData,
    isAuthenticated,
    filteredPackages: getFilteredPackages().length
  });

  return (
    <div 
      className="min-h-screen pt-20 transition-colors duration-300"
      style={{
        backgroundImage: `url('https://telnet.net.id/assets/bg.png')`,
        backgroundColor: isDarkMode ? '#111827' : '#dfe4e0ff',
        backgroundBlendMode: 'overlay'
      }}
    >
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
       
        {/* Error Message */}
        {error && (
          <div className={`mb-6 p-4 rounded-xl border ${
            isDarkMode 
              ? 'bg-yellow-500/20 border-yellow-500/50' 
              : 'bg-yellow-100 border-yellow-200'
          }`}>
            <div className="flex items-center space-x-3">
              <ExclamationTriangleIcon className={`h-6 w-6 ${
                isDarkMode ? 'text-yellow-400' : 'text-yellow-600'
              }`} />
              <div>
                <h4 className={`font-semibold ${
                  isDarkMode ? 'text-yellow-300' : 'text-yellow-800'
                }`}>
                  {error.includes('login') ? 'Autentikasi Diperlukan' : 'Peringatan'}
                </h4>
                <p className={`text-sm ${
                  isDarkMode ? 'text-yellow-200' : 'text-yellow-700'
                }`}>
                  {error}
                </p>
                {error.includes('login') && (
                  <button
                    onClick={() => window.location.href = '/login'}
                    className="mt-2 bg-[#1E9B4C] hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm"
                  >
                    Login Sekarang
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* New Order Success Message */}
        {newOrderMessage && (
          <div className={`mb-6 p-4 rounded-xl border ${
            isDarkMode 
              ? 'bg-green-500/20 border-green-500/50' 
              : 'bg-green-100 border-green-200'
          }`}>
            <div className="flex items-center space-x-3">
              <CheckCircleIcon className={`h-6 w-6 ${
                isDarkMode ? 'text-green-400' : 'text-green-600'
              }`} />
              <div>
                <h4 className={`font-semibold ${
                  isDarkMode ? 'text-green-300' : 'text-green-800'
                }`}>
                  Order Berhasil Dibuat!
                </h4>
                <p className={`text-sm ${
                  isDarkMode ? 'text-green-200' : 'text-green-700'
                }`}>
                  {newOrderMessage}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Paket Saya
              </h1>
              <p className={`mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Kelola dan pantau semua paket internet yang Anda miliki
              </p>
              {userData && (
                <p className={`mt-1 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  User: {userData.fullName || userData.email} 
                  {packages.length > 0 && ` (${packages.length} paket ditemukan)`}
                </p>
              )}
            </div>
            <div className="mt-4 sm:mt-0 flex space-x-3">
              <button 
                onClick={refreshData}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors inline-flex items-center space-x-2"
                disabled={loading}
              >
                <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards - Show if we have packages */}
        {packages.length > 0 && (
          <Status packages={packages} />
        )}

        {/* Tab Navigation - Show if we have packages */}
        {packages.length > 0 && (
          <div className="mb-6">
            <div className="flex flex-wrap gap-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setSelectedTab(tab.id)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    selectedTab === tab.id
                      ? 'bg-[#1E9B4C] text-white'
                      : isDarkMode
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {tab.name} ({tab.count})
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Package List */}
        {packages.length > 0 ? (
          <PackageList 
            packages={getFilteredPackages()} 
            isDarkMode={isDarkMode}
            getStatusBadge={getStatusBadge}
            getTypeIcon={getTypeIcon}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
          />
        ) : !loading && !error && (
          // Empty state when no packages found but no error
          <div className="text-center py-12">
            <div className={`mx-auto h-24 w-24 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
              <WifiIcon className="h-24 w-24" />
            </div>
            <h3 className={`mt-2 text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
              Belum ada paket
            </h3>
            <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              Anda belum memiliki paket internet. Silakan order paket baru.
            </p>
            <div className="mt-6">
              <button 
                onClick={() => window.location.href = '/produk'}
                className="bg-[#1E9B4C] hover:bg-green-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors inline-flex items-center space-x-2"
              >
                <WifiIcon className="h-5 w-5" />
                <span>Lihat Paket</span>
              </button>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default MyPackages;